<template>
    <div class="work-time-calendar-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ $t("report.agentWorkTimeCalendar") }}</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <div v-if="!tableMode">
                <UserStatusBarChart
                    :title="translatedTitle"
                    :user-names="calendarUsers().map((x) => x.name)"
                    :series="calculateSingleDayBarSeries()"
                    v-if="isOneDay()"
                />

                <div v-else class="moreThanDay">
                    <h5>{{ calendarTitle }}</h5>
                    <b-form-group class="mb-1" style="align-self: flex-end">
                        <b-form-select v-model="selectedCalendarUser" class="mb-3">
                            <option v-for="user in calendarUsers()" :key="user._id" :value="user._id">
                                {{ user.name }}
                            </option>
                        </b-form-select>
                    </b-form-group>
                    <UserStatusCalendarPieChart
                        :title="calendarTitle"
                        sub-title="Work Hour Distribution Daily"
                        :start-date="filters.startDate"
                        :end-date="filters.endDate"
                        :scatter-data="getCalendarScatterData()"
                        :pie-series="getCalendarPieSeries()"
                        v-if="selectedCalendarUser"
                    />
                    <SupsisButton primary ghost small @click="nextUser" class-names="btnLeft">
                        <i class="material-icons">chevron_left</i>
                    </SupsisButton>
                    <SupsisButton primary ghost small @click="prevUser" class-names="btnRight">
                        <i class="material-icons">chevron_right</i>
                    </SupsisButton>
                </div>
            </div>

            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import { mapState } from "vuex";
import fscreen from "fscreen";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import UserStatusBarChart from "../../Pages/Chatbot/pages/reports/charts/UserStatusBarChart.vue";
import UserStatusCalendarPieChart from "../../Pages/Chatbot/pages/reports/charts/UserStatusCalendarPieChart.vue";
import SupsisButton from "../../Pages/Settings/components/SupsisButton.vue";

const log = log2("WorkTimeCalendar");
export default Vue.extend({
    name: "WorkTimeCalendar",
    components: {
        SupsisButton,
        UserStatusCalendarPieChart,
        UserStatusBarChart,
        ChartDoddleIcon,
        TableView,
        IconButton,
    },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            selectedCalendarUser: null,
            calendarTitle: "",
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
        translatedTitle() {
            return this.$t("report.agentStatusHour");
        },
    },
    methods: {
        nextUser() {
            const users = this.calendarUsers();
            const index = users.findIndex((x) => x._id === this.selectedCalendarUser);
            if (index === users.length - 1) {
                this.selectedCalendarUser = users[0]._id;
            } else {
                this.selectedCalendarUser = users[index + 1]._id;
            }
        },
        prevUser() {
            const users = this.calendarUsers();
            const index = users.findIndex((x) => x._id === this.selectedCalendarUser);
            if (index === 0) {
                this.selectedCalendarUser = users[users.length - 1]._id;
            } else {
                this.selectedCalendarUser = users[index - 1]._id;
            }
        },
        getCalendarPieSeries() {
            const scatterData = this.getCalendarScatterData();
            const pieRadius = 30;
            const statusColors = {
                online: "rgb(27, 197, 189)",
                away: "rgb(54, 153, 255)",
                busy: "rgb(255, 168, 0)",
                offline: "rgb(246, 78, 96)",
                disconnected: "rgb(131,131,131)",
            };
            return scatterData.map((item, index) => {
                let statusMap = ["online", "away", "busy", "offline", "disconnected"].reduce((acc, cur) => {
                    const key = Object.keys(this.data).find((x) => {
                        return moment(x).format("YYYY-MM-DD") === item[0];
                    });
                    const foundItem = this.data[key].find((x) => x.userId === this.selectedCalendarUser);
                    acc[cur] = foundItem ? foundItem.status[cur] : 0;
                    return acc;
                }, {});
                const isAllZero = Object.values(statusMap).every((x) => x === 0);
                const fallbackStatusMap = {
                    online: 0,
                    away: 0,
                    busy: 0,
                    offline: 0,
                    disconnected: 24,
                };
                statusMap = isAllZero ? fallbackStatusMap : statusMap;

                return {
                    type: "pie",
                    id: "pie-" + index,
                    center: item[0],
                    radius: pieRadius,
                    coordinateSystem: "calendar",
                    label: {
                        formatter: "{c}",
                        position: "inside",
                        show: false,
                    },
                    data: [
                        { name: "Online", value: statusMap.online, color: statusColors.online },
                        { name: "Away", value: statusMap.away, color: statusColors.away },
                        { name: "Busy", value: statusMap.busy, color: statusColors.busy },
                        { name: "Offline", value: statusMap.offline, color: statusColors.offline },
                        { name: "Disconnected", value: statusMap.disconnected, color: statusColors.disconnected },
                    ],
                };
            });
        },
        getCalendarScatterData() {
            let data = Object.keys(this.data).map((x) => [
                moment(x).format("YYYY-MM-DD"),
                Math.ceil(Math.random() * 100),
            ]);
            data = _.orderBy(data, [0], ["asc"]);
            return data;
        },
        calculateSingleDayBarSeries() {
            const users = this.calendarUsers();
            const headBucketItems = _.head(Object.values(this.data));

            return ["online", "away", "busy", "offline", "disconnected"].map((status) => {
                return {
                    name: this.$t(`message.${status}`),
                    type: "bar",
                    stack: "total",
                    emphasis: {
                        focus: "series",
                    },
                    data: users.map((user) => {
                        const maybeUser = headBucketItems.find((x) => x.userId === user._id);
                        return maybeUser ? maybeUser.status[status] : 0;
                    }),
                };
            });
        },

        calendarUsers() {
            let allUserIds = Object.values(this.data)
                .map((xs) => xs.map((x) => x.userId))
                .flat();
            allUserIds = _.uniq(allUserIds);
            let users = this.users.filter((x) => allUserIds.includes(x._id));
            users = _.orderBy(users, ["name"], ["asc"]);
            return users;
        },
        updateCalendarTitle() {
            log.debug("updateCalendarTitle");
            const calendarUsers = this.calendarUsers();
            const buckets = Object.keys(this.data);

            const maybeSelectedCalendarUserName = this.selectedCalendarUser
                ? calendarUsers.find((x) => x._id === this.selectedCalendarUser)
                    ? calendarUsers.find((x) => x._id === this.selectedCalendarUser).name
                    : undefined
                : undefined;
            let title = "";
            if (maybeSelectedCalendarUserName) {
                const name = _.startCase(maybeSelectedCalendarUserName.trim().toLowerCase());
                const sDate = moment(_.head(buckets)).format("D MMMM");
                const eDate = moment(_.last(buckets)).format("D MMMM");
                if (sDate !== eDate) {
                    title = this.$t("message.workSchedule", {
                        name,
                        sDate,
                        eDate,
                    });
                } else {
                    title = this.$t("message.workScheduleOneDay", {
                        name,
                        sDate,
                    });
                }
            }
            this.calendarTitle = title;
        },
        isOneDay() {
            const start = moment(this.filters.startDate);
            const end = moment(this.filters.endDate);
            const result = end.diff(start, "days") === 0;
            log("isOneDay", result);
            return result;
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".work-time-calendar-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("report.user"),
                    dataIndex: "user",
                    key: "user",
                },
                {
                    title: this.$t("report.resolveTime"),
                    dataIndex: "resolveTime",
                    key: "resolveTime",
                },
            ];
        },
        getRows() {
            return [];
        },

        exportToExcel() {
            let items = [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });
            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    User: item.user?.name || "Unknown User",
                    "Avg Resolve Time": item.avgResolveTime,
                };
            });
            expertToExcel(excelData, `chat_session_count_by_users.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".work-time-calendar-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
        if (!this.isOneDay()) {
            this.selectedCalendarUser = this.calendarUsers()[0]?._id;
            this.updateCalendarTitle();
        }
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        data: {
            handler(val) {
                log.info("data changed", val);
                if (!this.isOneDay()) {
                    if (!this.selectedCalendarUser) {
                        this.selectedCalendarUser = this.calendarUsers()[0]?._id;
                    }
                    this.updateCalendarTitle();
                }
            },
            deep: true,
        },
        selectedCalendarUser(newVal, oldVal) {
            log.debug("selectedCalendarUser changed", newVal);
            this.updateCalendarTitle();
        },
        filters: {
            handler(val) {
                log.info("filters changed", val);
                if (!this.isOneDay()) {
                    if (!this.selectedCalendarUser) {
                        this.selectedCalendarUser = this.calendarUsers()[0]?._id;
                    }
                    this.updateCalendarTitle();
                }
            },
            deep: true,
        },
    },
});
</script>
<style lang="scss">
.work-time-calendar-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.work-time-calendar-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .supsis-button {
        &.btnLeft {
            position: absolute;
            left: 0;
            top: 50%;
        }

        &.btnRight {
            position: absolute;
            right: 0;
            top: 50%;
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
